import { useCallback, useEffect, useState } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { CircularProgress, Grid, TextField, Typography } from '@vapor/material'
import { AutocompleteTextField, FormItem } from 'hic-components'
import { CustomerInput, CustomerType, SimpleCustomerLookupFragment, useCustomerQuery, useCustomerUpdateMutation } from 'src/api/api.generated'
import { useTranslation } from 'react-i18next'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import * as z from 'zod'
import { isEmail, isPhoneNumber } from 'src/utils/contact'
import { useTenant } from 'src/app/hooks/context/context.hook'
import { Backdrop } from '@mui/material'
import { Language, languageList, localizedLanguageKeys } from 'src/api/language'

type CustomerDetailsEditProps = {
  value: SimpleCustomerLookupFragment | null
  type: CustomerType
  onChange: (value: SimpleCustomerLookupFragment | null) => void
  onErrors?: (errors: any) => void
  requireEmailAddress?: boolean
}

export const CustomerDetailsEdit = (props: CustomerDetailsEditProps) => {
  const { value, type, onChange } = props
  const { t } = useTranslation()
  const tenant = useTenant()

  // State
  const [loading, setLoading] = useState<boolean>()

  // API
  const [updateCustomerMutation] = useCustomerUpdateMutation()
  const { refetch, loading: getCustomerLoading } = useCustomerQuery({ variables: { tenantId: tenant.id, customerId: value?.id } })

  // RHF
  const customerForm = z
    .object({
      name: z.string(),
      lastName: z.string(),
      firstName: z.string(),
      mailContact: z
        .string()
        .optional()
        .superRefine((data, ctx) => {
          if (!data) return
          if (!isEmail(data)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t('validation.invalid_email'),
            })
          }
        }),
      phoneContact: z
        .string()
        .optional()
        .superRefine((data, ctx) => {
          if (!data) return
          if (!isPhoneNumber(data)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t('validation.invalid_phone_number'),
            })
          }
        }),
      language: z.enum(Language),
    })
    .superRefine((data, ctx) => {
      if (props.requireEmailAddress && (!data.mailContact || data.mailContact?.length < 1))
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('commons.required_field'),
          path: ['mailContact'],
        })
    })

  type CustomerForm = z.infer<typeof customerForm>

  const {
    control,
    handleSubmit,
    setValue,
    trigger,
    formState: { errors },
  } = useForm<CustomerForm>({ resolver: zodResolver(customerForm) })

  // Events
  const onSubmit = async (data: CustomerForm): Promise<void> => {
    if ((type === 'PERSON' && !data.lastName) || (type === 'COMPANY' && !data.name)) {
      onChange(null)
    } else {
      const result = await refetch()
      if (result.data.customers && result.data.customers.length === 1) {
        const dbCustomer = result.data.customers[0]

        const updatedCustomer: CustomerInput = {
          language: data.language,
          tenantId: dbCustomer.tenantId,
          type: dbCustomer.type,
          name: type === 'PERSON' ? `${data.lastName} ${data.firstName}` : data.name,
          firstName: data.firstName,
          lastName: data.lastName,
          mailContacts: dbCustomer.mailContacts?.find((c) => c.type === 'MAIN')
            ? [...dbCustomer.mailContacts.map((c) => (c.type === 'MAIN' && data.mailContact ? { type: c.type, isDefault: c.isDefault, order: c.order, value: data.mailContact } : c))]
            : data.mailContact
            ? [{ type: 'MAIN', isDefault: true, order: 1, value: data.mailContact }]
            : undefined,
          phoneContacts: dbCustomer.phoneContacts?.find((c) => c.type === 'MAIN')
            ? [...dbCustomer.phoneContacts.map((c) => (c.type === 'MAIN' && data.phoneContact ? { type: c.type, isDefault: c.isDefault, order: c.order, value: data.phoneContact } : c))]
            : data.phoneContact
            ? [{ type: 'MAIN', isDefault: true, order: 1, value: data.phoneContact }]
            : undefined,
        }

        try {
          setLoading(true)
          await updateCustomerMutation({
            variables: {
              input: updatedCustomer,
              id: dbCustomer.id,
            },
            onCompleted: () => {
              if (value) {
                onChange({
                  ...value,
                  id: dbCustomer.id,
                  type: dbCustomer.type,
                  name: updatedCustomer.name,
                  lastName: updatedCustomer.lastName,
                  firstName: updatedCustomer.firstName,
                  mailContacts: updatedCustomer.mailContacts,
                  phoneContacts: updatedCustomer.phoneContacts,
                  language: updatedCustomer.language,
                })
              }
            },
          })
        } catch (error) {
          console.log({ error })
        } finally {
          setLoading(false)
        }
      }
    }
  }

  const setCustomer = useCallback(
    (customer: SimpleCustomerLookupFragment) => {
      setValue('name', customer.name)
      setValue('lastName', customer.lastName || '')
      setValue('firstName', customer.firstName || '')
      setValue('mailContact', customer.mailContacts && customer.mailContacts.length > 0 ? customer.mailContacts[0].value : '')
      setValue('phoneContact', customer.phoneContacts && customer.phoneContacts.length > 0 ? customer.phoneContacts[0].value : '')
      setValue('language', customer.language || languageList.it)
    },
    [setValue],
  )

  const submitHandler: SubmitHandler<CustomerForm> = useCallback(
    async (data: CustomerForm) => {
      await onSubmit(data)
    },
    [onSubmit],
  )

  useEffect(() => {
    if (value !== null) setCustomer(value)
  }, [value])

  useEffect(() => {
    trigger('mailContact')
  }, [props.requireEmailAddress])

  useEffect(() => {
    if (errors && props.onErrors) props.onErrors(errors)
  }, [errors])

  const onFormSubmit = useCallback(handleSubmit(submitHandler), [handleSubmit, submitHandler])

  return (
    <>
      <Backdrop open={(loading || getCustomerLoading) ?? false}>
        <CircularProgress />
      </Backdrop>
      <form onBlur={onFormSubmit}>
        <Grid container spacing={2} paddingY={1}>
          {type === 'COMPANY' ? (
            <Grid item xs={12}>
              <Controller<CustomerForm, 'name'>
                name={'name'}
                control={control}
                render={({ field }) => (
                  <FormItem fullWidth fieldLabel={t('pms.common.customer.company_name')}>
                    <TextField {...field} size="small" value={field.value || ''} inputRef={field.ref} />
                  </FormItem>
                )}
              />
            </Grid>
          ) : (
            <>
              <Grid item xs={6}>
                <Controller<CustomerForm, 'lastName'>
                  name={'lastName'}
                  control={control}
                  render={({ field }) => (
                    <FormItem fullWidth fieldLabel={t('pms.common.customer.lastName')}>
                      <TextField {...field} size="small" value={field.value || ''} inputRef={field.ref} />
                    </FormItem>
                  )}
                />
              </Grid>
              <Grid item xs={6}>
                <Controller<CustomerForm, 'firstName'>
                  name={'firstName'}
                  control={control}
                  render={({ field }) => (
                    <FormItem fullWidth fieldLabel={t('pms.common.customer.firstName')}>
                      <TextField {...field} size="small" value={field.value || ''} inputRef={field.ref} />
                    </FormItem>
                  )}
                />
              </Grid>
            </>
          )}
          <Grid item xs={6}>
            <Controller<CustomerForm, 'mailContact'>
              name={'mailContact'}
              control={control}
              render={({ field }) => (
                <FormItem errorMessage={errors.mailContact?.message} fullWidth fieldLabel={t('pms.common.customer.mail')}>
                  <TextField {...field} size="small" value={field.value || ''} inputRef={field.ref} />
                </FormItem>
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Controller<CustomerForm, 'phoneContact'>
              name={'phoneContact'}
              control={control}
              render={({ field }) => (
                <FormItem fullWidth fieldLabel={t('pms.common.customer.phone')}>
                  <TextField {...field} size="small" value={field.value || ''} inputRef={field.ref} />
                </FormItem>
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller<CustomerForm, 'language'>
              name={'language'}
              control={control}
              render={({ field }) => (
                <FormItem //
                  fullWidth
                  fieldLabel={`${t('pms.common.customer.contact_language')}`}
                >
                  <AutocompleteTextField<Language> //
                    getOptionLabel={(option: Language) => `${languageList[option].nativeName} (${languageList[option].name})`}
                    options={[...localizedLanguageKeys]}
                    value={field.value || null}
                    onChange={field.onChange}
                    disabled={loading}
                    inputRef={field.ref}
                    size="small"
                  />
                </FormItem>
              )}
            />
          </Grid>
        </Grid>
      </form>
    </>
  )
}
